import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Panel, Overline, ProgressBar, ProgressRing, Btn, Pill, Icon, EmptyState, PageLoader, SectionTitle, gradFor } from "../../components/ui-kit";
import { cn } from "../../lib/utils";

const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };

export default function StudentHome() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/student/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <PageLoader />;
  const primary = d.continue_learning[0];

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-8">
      {/* Greeting */}
      <div className="flex flex-wrap items-center justify-between gap-4 pt-1">
        <div>
          <div className="text-mim-blue text-sm font-bold">{greeting()}, {d.name.split(" ")[0]} 👋</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-mim-navy tracking-tight mt-1">Let's continue learning</h1>
          <p className="text-mim-muted mt-1">You're making great progress. Pick up where you left off.</p>
        </div>
        <div className="flex gap-3">
          <div className="flex items-center gap-2.5 bg-mim-yellow/12 rounded-2xl px-4 py-2.5">
            <Icon name="Flame" className="w-5 h-5 text-mim-yellow" />
            <div><div className="text-lg font-bold text-mim-navy leading-none">{d.streak}</div><div className="text-[10px] font-semibold text-mim-muted uppercase tracking-wide">day streak</div></div>
          </div>
          <div className="flex items-center gap-2.5 bg-mim-purple/8 rounded-2xl px-4 py-2.5">
            <Icon name="Zap" className="w-5 h-5 text-mim-purple" />
            <div><div className="text-lg font-bold text-mim-navy leading-none">{d.xp}</div><div className="text-[10px] font-semibold text-mim-muted uppercase tracking-wide">XP earned</div></div>
          </div>
        </div>
      </div>

      {/* Hero bento: dominant Continue + mastery + goal */}
      <div className="grid lg:grid-cols-3 gap-5">
        {primary ? (
          <Link to={`/topic/${primary.topic_id}`} className="lg:col-span-2 group">
            <div className="relative rounded-[28px] p-7 sm:p-9 text-white overflow-hidden h-full hover-lift" style={{ background: gradFor(primary.subject_color) }}>
              <div className="absolute inset-0 dotgrid opacity-60" />
              <Icon name="PlayCircle" className="absolute -right-6 -bottom-6 w-52 h-52 opacity-10" />
              <div className="relative">
                <Pill tone="white" icon="BookOpen">Continue Learning</Pill>
                <div className="text-white/70 text-sm font-semibold mt-5">{primary.subject_name}</div>
                <h2 className="text-3xl sm:text-4xl font-bold mt-1 tracking-tight">{primary.topic_name}</h2>
                <div className="flex items-center gap-5 mt-5 text-sm text-white/80">
                  <span className="inline-flex items-center gap-1.5"><Icon name="Clock" className="w-4 h-4" />~8 min left</span>
                  <span className="inline-flex items-center gap-1.5"><Icon name="Target" className="w-4 h-4" />{primary.progress_pct}% complete</span>
                </div>
                <div className="mt-3 max-w-md"><ProgressBar value={primary.progress_pct} color="#ffffff" track="rgba(255,255,255,0.25)" /></div>
                <div className="inline-flex items-center gap-2 bg-white text-mim-navy font-semibold text-sm px-5 py-3 rounded-2xl mt-6 group-hover:gap-3 transition-all">
                  Continue Learning <Icon name="ArrowRight" className="w-4 h-4" />
                </div>
              </div>
            </div>
          </Link>
        ) : (
          <Panel tint="soft" className="lg:col-span-2"><EmptyState icon="BookOpen" title="Start your first topic" subtitle="Head to the Learn hub to begin." action={<Link to="/learn"><Btn>Explore subjects</Btn></Link>} /></Panel>
        )}

        <div className="grid grid-rows-2 gap-5">
          <div className="rounded-[28px] p-6 bg-mim-navy text-white relative overflow-hidden flex items-center gap-5">
            <div className="absolute inset-0 dotgrid opacity-50" />
            <ProgressRing value={d.mastery} color="#19B9D8" track="rgba(255,255,255,0.15)" size={86} label={`${d.mastery}`} sublabel="Mastery" labelClass="text-white" />
            <div className="relative"><div className="font-bold">Learning Health</div><p className="text-white/60 text-xs mt-1">Overall mastery across all subjects</p></div>
          </div>
          {d.goals[0] && (
            <div className="rounded-[28px] p-6 bg-mim-green/8 border border-mim-green/15">
              <div className="flex items-center justify-between"><Overline className="text-mim-green">Today's Goal</Overline><Pill tone="green">{d.goals[0].progress}/{d.goals[0].target}{d.goals[0].unit}</Pill></div>
              <div className="font-bold text-mim-navy mt-2 mb-3">{d.goals[0].title}</div>
              <ProgressBar value={(d.goals[0].progress / d.goals[0].target) * 100} color="var(--mim-green)" />
            </div>
          )}
        </div>
      </div>

      {/* Continue strip (rest) + Ask AI */}
      <div className="grid lg:grid-cols-3 gap-5">
        {d.continue_learning.slice(1).map((c) => (
          <Link key={c.topic_id} to={`/topic/${c.topic_id}`}>
            <Panel hover className="h-full">
              <div className="flex items-center gap-2 mb-3"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c.subject_color }} /><span className="text-xs font-semibold text-mim-muted">{c.subject_name}</span></div>
              <h4 className="font-bold text-mim-navy text-lg">{c.topic_name}</h4>
              <div className="flex justify-between text-xs text-mim-muted mt-4 mb-1.5"><span>Progress</span><span className="font-semibold text-mim-navy">{c.progress_pct}%</span></div>
              <ProgressBar value={c.progress_pct} color={c.subject_color} />
            </Panel>
          </Link>
        ))}
        <Link to="/ask-ai" className={d.continue_learning.length > 2 ? "" : "lg:col-span-3"}>
          <div className="rounded-3xl p-6 text-white relative overflow-hidden hover-lift h-full" style={{ background: "linear-gradient(135deg,#8a6df0,#5f3fd0)" }}>
            <div className="absolute inset-0 dotgrid opacity-50" />
            <Icon name="Sparkles" className="absolute right-5 top-5 w-6 h-6 opacity-80" />
            <div className="relative flex flex-col h-full justify-between">
              <div><div className="text-xs font-bold uppercase tracking-widest text-white/80">Ask Mimi AI</div><div className="text-xl font-bold mt-2">Stuck on something? Get a visual explanation instantly.</div></div>
              <div className="inline-flex items-center gap-2 font-semibold text-sm mt-4">Ask AI <Icon name="ArrowRight" className="w-4 h-4" /></div>
            </div>
          </div>
        </Link>
      </div>

      {/* Subjects strip */}
      <div>
        <SectionTitle title="Your Subjects" icon="LayoutGrid" action={<Link to="/learn" className="text-sm font-semibold text-mim-blue hover:underline">View all →</Link>} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
          {d.subjects.map((s) => (
            <Link key={s.id} to={`/learn/subject/${s.id}`}>
              <div className="rounded-3xl p-5 text-white relative overflow-hidden hover-lift h-full" style={{ background: gradFor(s.color) }}>
                <div className="absolute inset-0 dotgrid opacity-40 pointer-events-none" />
                <div className="relative z-10">
                  <div className="w-10 h-10 rounded-xl glass flex items-center justify-center"><Icon name={s.icon} className="w-5 h-5" /></div>
                  <div className="font-bold text-lg mt-3">{s.name}</div>
                  <div className="text-xs text-white/75 mt-0.5">{s.mastery}% mastery</div>
                  <div className="mt-3"><ProgressBar value={s.progress} color="#ffffff" track="rgba(255,255,255,0.25)" height="h-1.5" /></div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Weak + Recommended */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <SectionTitle title="Needs Practice" icon="Target" />
          <div className="space-y-3">
            {d.weak_areas.length === 0 && <Panel tint="soft"><EmptyState icon="PartyPopper" title="No weak areas!" subtitle="You're on top of everything." tone="green" /></Panel>}
            {d.weak_areas.map((w) => (
              <div key={w.id} className="flex items-center justify-between gap-4 rounded-3xl p-5 bg-mim-coral/6 border border-mim-coral/15">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><h4 className="font-semibold text-mim-navy truncate">{w.name}</h4><Pill tone="coral">{w.accuracy}%</Pill></div>
                  <p className="text-xs text-mim-muted mt-1">{w.reason}</p>
                </div>
                <Link to={`/topic/${w.topic_id}`}><Btn variant="coral" className="shrink-0" icon="Dumbbell">Practice</Btn></Link>
              </div>
            ))}
          </div>
        </div>
        <div>
          <SectionTitle title="Recommended for You" icon="Wand2" />
          <div className="space-y-3">
            {d.recommendations.map((r) => (
              <Link key={r.id} to={`/topic/${r.topic_id}`}>
                <Panel hover className="flex items-center gap-3">
                  <div className={cn("w-11 h-11 rounded-2xl flex items-center justify-center shrink-0",
                    r.type === "practice" ? "bg-mim-coral/10 text-mim-coral" : r.type === "revise" ? "bg-mim-yellow/15 text-[#b07d13]" : "bg-mim-soft text-mim-blue")}>
                    <Icon name={r.type === "practice" ? "Dumbbell" : r.type === "revise" ? "RotateCcw" : "PlayCircle"} className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0"><h4 className="font-semibold text-mim-navy truncate">{r.title}</h4><p className="text-xs text-mim-muted truncate">{r.reason}</p></div>
                  <Icon name="ChevronRight" className="w-5 h-5 text-mim-muted shrink-0" />
                </Panel>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Upcoming */}
      {(d.upcoming_tests.length > 0 || d.assignments.length > 0) && (
        <div>
          <SectionTitle title="Upcoming" icon="CalendarClock" />
          <div className="grid sm:grid-cols-2 gap-3">
            {d.upcoming_tests.map((t) => (
              <Panel key={t.id} className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-mim-coral/10 text-mim-coral flex items-center justify-center shrink-0"><Icon name="FileText" className="w-5 h-5" /></div>
                <div className="flex-1"><h4 className="font-semibold text-mim-navy">{t.title}</h4><p className="text-xs text-mim-muted">{new Date(t.date).toLocaleDateString()} · {t.total_marks} marks</p></div>
                <Pill tone="coral">Test</Pill>
              </Panel>
            ))}
            {d.assignments.map((a) => (
              <Panel key={a.id} className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-mim-yellow/15 text-[#b07d13] flex items-center justify-center shrink-0"><Icon name="ClipboardList" className="w-5 h-5" /></div>
                <div className="flex-1"><h4 className="font-semibold text-mim-navy">{a.title}</h4><p className="text-xs text-mim-muted">Due {new Date(a.due).toLocaleDateString()} · {a.marks} marks</p></div>
                <Pill tone="yellow">Assignment</Pill>
              </Panel>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
